import { type FormEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Alert, AuthLayout, Button, Field, TextLink } from '../components/ui';
import { authClient, authErrorMessage } from '../lib/auth-client';
import { useSession } from '../lib/session';
import { safeNext } from './SignIn';

/** Second sign-in step for accounts with two-factor on: app code or a backup code. */
export function TwoFactorChallenge() {
  const { refresh } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [useBackup, setUseBackup] = useState(false);
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const trimmed = code.replace(/\s/g, '');
    const { error: failed } = useBackup
      ? await authClient.twoFactor.verifyBackupCode({ code: trimmed, trustDevice })
      : await authClient.twoFactor.verifyTotp({ code: trimmed, trustDevice });
    setBusy(false);
    if (failed) return setError(authErrorMessage(failed));
    await refresh();
    navigate(safeNext(params.get('next')));
  }

  return (
    <AuthLayout
      title="Two-factor check"
      subtitle={
        useBackup
          ? 'Enter one of the backup codes you saved when you turned on two-factor.'
          : 'Enter the 6-digit code from your authenticator app.'
      }
      footer={<TextLink to="/sign-in">Start over</TextLink>}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <Field
          label={useBackup ? 'Backup code' : 'Code'}
          name="code"
          autoComplete="one-time-code"
          inputMode={useBackup ? 'text' : 'numeric'}
          pattern={useBackup ? undefined : '[0-9 ]{6,7}'}
          required
          autoFocus
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
        <label className="flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={trustDevice}
            onChange={(event) => setTrustDevice(event.target.checked)}
            className="size-4 accent-ink"
          />
          Don’t ask again on this device for 30 days
        </label>
        <Button busy={busy}>Continue</Button>
        <button
          type="button"
          onClick={() => {
            setUseBackup(!useBackup);
            setCode('');
            setError(null);
          }}
          className="w-full text-center text-sm font-semibold text-ink underline decoration-brand decoration-2 underline-offset-4"
        >
          {useBackup ? 'Use my authenticator app instead' : 'Lost your phone? Use a backup code'}
        </button>
      </form>
    </AuthLayout>
  );
}
